import type {
    ServerNode,
    ServerEdge,
    ServerGraph,
    WorkflowMetadata,
    WorkflowPayload,
    WorkflowCreatePayload,
    WorkflowUpdatePayload,
} from './types';
import type { StepDefinition } from './workflowParser';
import { Trigger } from '../nodes/TriggerNode';
import { getActionStrategy } from './actionStrategies';
import { getConditionStrategy } from './conditionStrategies';
import { ACTION_CODE_TO_FORM_TYPE } from '../utils/actionCodeMapping';

const NODE_X_SPACING = 200;

/**
 * Converts the in-memory StepDefinition[] into the server workflow JSON payload.
 *
 * - For creation (no workflowId): returns WorkflowCreatePayload
 * - For update (has workflowId): returns WorkflowUpdatePayload
 *
 * Only configured (non-placeholder) steps are serialized.
 */
export function serializeStepsToWorkflow(
    steps: StepDefinition[],
    metadata: WorkflowMetadata
): WorkflowPayload {
    const configuredSteps = steps.filter((s) => !isStepPlaceholder(s));

    const nodes: ServerNode[] = [];
    const edges: ServerEdge[] = [];

    for (let i = 0; i < configuredSteps.length; i++) {
        const step = configuredSteps[i];

        nodes.push({
            id: step.id,
            type: resolveServerNodeType(step),
            config: resolveServerConfig(step),
            position: { x: i * NODE_X_SPACING, y: 0 },
        });

        if (i + 1 < configuredSteps.length) {
            const nextId = configuredSteps[i + 1].id;
            const edgeWhen = step.type === 'condition' ? ('TRUE' as const) : ('ALWAYS' as const);
            edges.push({ from: step.id, to: nextId, when: edgeWhen });
        }

        if (step.type === 'condition') {
            edges.push({ from: step.id, to: null, when: 'FALSE' });
        }
    }

    const entryNodeId = configuredSteps.length > 0 ? configuredSteps[0].id : '';

    const graph: ServerGraph = {
        entryNodeId,
        nodes,
        edges,
    };

    if (metadata.workflowId) {
        const payload: WorkflowUpdatePayload = {
            workflowId: metadata.workflowId,
            name: metadata.name,
            triggerType: metadata.triggerType,
            applyOncePerContact: metadata.applyOncePerContact ?? false,
            status: metadata.status,
            revision: metadata.revision,
            graph,
        };
        return payload;
    }

    const payload: WorkflowCreatePayload = {
        name: metadata.name,
        triggerType: metadata.triggerType,
        applyOncePerContact: metadata.applyOncePerContact ?? false,
        graph,
    };
    return payload;
}

// ---------------------------------------------------------------------------
// Step → server node type resolution
// ---------------------------------------------------------------------------

function resolveServerNodeType(step: StepDefinition): string {
    if (step.type === 'trigger') {
        return step.data.triggerType === Trigger.Message.Outbound
            ? 'Trigger.Message.Outbound'
            : 'Trigger.Message.Inbound';
    }

    if (step.type === 'condition') {
        return step.data.conditionCode ?? 'Condition.Message.KeywordMatch';
    }

    if (step.data.actionCode) return step.data.actionCode;

    const formTypeToCode = Object.fromEntries(
        Object.entries(ACTION_CODE_TO_FORM_TYPE).map(([code, formType]) => [formType, code])
    );
    return formTypeToCode[step.data.actionType ?? ''] ?? 'Action.Message.AutomatedResponse';
}

// ---------------------------------------------------------------------------
// Step data → server config resolution
// ---------------------------------------------------------------------------

function resolveServerConfig(step: StepDefinition): Record<string, unknown> {
    if (step.type === 'trigger') return {};
    if (step.type === 'condition') {
        const strategy = getConditionStrategy(step.data.conditionCode);
        return strategy ? strategy.serialize(step.data) : {};
    }
    const strategy = getActionStrategy(step.data.actionCode);
    return strategy ? strategy.serialize(step.data) : {};
}

function isStepPlaceholder(step: StepDefinition): boolean {
    if (step.type === 'trigger') return false;
    return step.data.isPlaceholder ?? false;
}
